import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Fuse from "fuse.js";
import "./DataHome.css";
import linksData from "./Data/AllDataGalleryData.json";
import RelatedBriefsForDatavis from "./RelatedBriefsForDatavis";
import RelatedPodcastsForDatavis from "./RelatedPodcastsForDatavis";
import RelatedDatavis from "./RelatedDatavis";
import RelatedNovelsForDatavis from "./RelatedNovelsForDatavis";
import RelatedArchivesForDatavis from "./RelatedArchivesForDatavis";

// Define TagButton outside of GalleryFilterBar to avoid redefinition on each render
const TagButton = ({ category, children, isSelected, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex",
      padding: "6px 12px",
      alignItems: "center",
      gap: "4px",
      borderRadius: "8px",
      border: "0.25px solid var(--Gray-Black, #00000090)",
      background: isSelected ? "white" : "transparent",
      cursor: "pointer",
      color: "#00000090",
    }}
  >
    {children}
  </button>
);

const DataHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const initialCategory = queryParams.get("category")
    ? decodeURIComponent(queryParams.get("category"))
    : null;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(
    initialCategory ? { [initialCategory]: true } : {}
  );
  const [filteredLinks, setFilteredLinks] = useState(linksData);
  const [iframeUrl, setIframeUrl] = useState("");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isIntermediateView, setIsIntermediateView] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isCitationVisible, setIsCitationVisible] = useState(false);
  const [citationStyle, setCitationStyle] = useState("MLA");

  const uniqueCategories = useMemo(
    () => Array.from(new Set(linksData.flatMap((link) => link.categories))),
    [linksData]
  );

  // Fuse.js search and filtering logic
  useEffect(() => {
    const fuse = new Fuse(linksData, {
      keys: ["title", "categories"],
      threshold: 0.7,
    });

    const result = fuse.search(searchTerm);
    const resultLinks = searchTerm ? result.map(({ item }) => item) : linksData;

    const filtered = resultLinks.filter((link) =>
      Object.keys(selectedCategories)
        .filter((cat) => selectedCategories[cat])
        .every((cat) => link.categories.includes(cat))
    );

    setFilteredLinks(filtered);

    if (id) {
      const data = linksData.find((link) => link.id === id);
      if (data) {
        setSelectedLink(data);
        setIsIntermediateView(true);
      }
    } else {
      setIsIntermediateView(false);
      setSelectedLink(null);
    }
  }, [searchTerm, selectedCategories, id]);

  // Handle browser back button
  useEffect(() => {
    const handleBackNavigation = () => {
      if (isIntermediateView) {
        setIsIntermediateView(false);
        setSelectedLink(null);
        navigate("/datavis", { replace: true });
      }
    };

    window.addEventListener("popstate", handleBackNavigation);

    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  }, [isIntermediateView, navigate]);

  // Handle body overflow when pop-up is visible
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isPopupVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalOverflow;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isPopupVisible]);

  // Handle Tag button click to filter by category
  const handleTagButtonClick = (category) => {
    navigate(`/datavis?category=${encodeURIComponent(category)}`);
    setSelectedCategories({ [category]: true });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) => {
      const newState = { ...prev, [category]: !prev[category] };
      const activeCategories = Object.keys(newState).filter(
        (key) => newState[key]
      );
      return activeCategories.length === 0 ? {} : newState;
    });
  };

  const handleShowAllClick = () => {
    setSelectedCategories({});
  };

  const handleLinkClick = (link) => {
    setSelectedLink(link);
    setIsIntermediateView(true);
    navigate(`/datavis/${link.id}`);
  };

  const handleIntermediateLinkClick = (url, id) => {
    const newTabUrl = `${window.location.origin}/datavis/${id}#expanded`;
    window.open(newTabUrl, "_blank"); // Open in a new tab with the expanded hash
  };

  useEffect(() => {
    const hash = window.location.hash; // Get the URL fragment
    const currentId = id; // Get the ID from useParams

    // Check if the URL has `#expanded` and if an ID is present
    if (hash === "#expanded" && currentId) {
      const data = linksData.find((link) => link.id === currentId);
      if (data) {
        setSelectedLink(data); // Set the selected link
        setIframeUrl(data.url); // Set the URL to display in the iframe
        setIsPopupVisible(true); // Open the pop-up automatically
      }
    }
  }, [id]); // This ensures it runs when the component loads with a specific ID

  const closePopup = () => {
    setIsPopupVisible(false);
    window.history.replaceState(null, null, window.location.pathname); // Remove #expanded from URL
  };

  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    if (window.pageYOffset > 500) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility);
    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  const handleCiteClick = () => {
    setIsCitationVisible(true);
  };

  const handleCloseCitation = () => {
    setIsCitationVisible(false);
  };

  const handleCopyToClipboard = () => {
    const citationText = generateCitationText(citationStyle);
    navigator.clipboard.writeText(citationText).then(
      () => {
        alert("Citation copied to clipboard!");
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const generateCitationText = (style) => {
    const {
      title,
      content_creator: author,
      year,
      date,
      technical_designer,
      url,
    } = selectedLink;
    const fullUrl = window.location.origin + url;

    switch (style) {
      case "MLA":
        return `${author}. “${title}.” Created by ${technical_designer}, ${year}. ${fullUrl}. Accessed ${new Date().toLocaleDateString()}.`;
      case "APA":
        return `${author}. (${new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}). ${title}. ${fullUrl}`;
      case "Chicago":
        return `${author}. “${title}”. Created by ${technical_designer}, ${year}. ${fullUrl}.`;
      default:
        return "";
    }
  };

  const generateCitation = (style) => {
    const citationText = generateCitationText(style);
    const [author, title, ...rest] = citationText.split(/(?<=\.|\?)\s/);

    return (
      <>
        {author} <i>{title}</i> {rest.join(" ")}
      </>
    );
  };

  return (
    <div className="GalleryWrapper">
      {isVisible && (
        <button
          className="back-to-top-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
        >
          <span className="back-to-top-text">Back to Top</span>
        </button>
      )}

      <div className="filter-bar">
        <div className="filter-bar-header">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="18"
            viewBox="0 0 22 18"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M8.25281 2.81348C8.25281 2.26647 8.47011 1.74186 8.8569 1.35507C9.24369 0.968275 9.7683 0.750977 10.3153 0.750977H11.6903C12.2373 0.750977 12.7619 0.968275 13.1487 1.35507C13.5355 1.74186 13.7528 2.26647 13.7528 2.81348V4.18848C13.7528 4.73549 13.5355 5.26009 13.1487 5.64688C12.7619 6.03368 12.2373 6.25098 11.6903 6.25098V7.62598H19.2528C19.4351 7.62598 19.61 7.69841 19.7389 7.82734C19.8679 7.95627 19.9403 8.13114 19.9403 8.31348V9.68848C19.9403 9.87081 19.8679 10.0457 19.7389 10.1746C19.61 10.3035 19.4351 10.376 19.2528 10.376C19.0705 10.376 18.8956 10.3035 18.7667 10.1746C18.6377 10.0457 18.5653 9.87081 18.5653 9.68848V9.00098H11.6903V9.68848C11.6903 9.87081 11.6179 10.0457 11.4889 10.1746C11.36 10.3035 11.1851 10.376 11.0028 10.376C10.8205 10.376 10.6456 10.3035 10.5167 10.1746C10.3877 10.0457 10.3153 9.87081 10.3153 9.68848V9.00098H3.44031V9.68848C3.44031 9.87081 3.36787 10.0457 3.23894 10.1746C3.11001 10.3035 2.93514 10.376 2.75281 10.376C2.57047 10.376 2.3956 10.3035 2.26667 10.1746C2.13774 10.0457 2.06531 9.87081 2.06531 9.68848V8.31348C2.06531 8.13114 2.13774 7.95627 2.26667 7.82734C2.3956 7.69841 2.57047 7.62598 2.75281 7.62598H10.3153V6.25098C9.7683 6.25098 9.24369 6.03368 8.8569 5.64688C8.47011 5.26009 8.25281 4.73549 8.25281 4.18848V2.81348ZM11.6903 4.87598C11.8726 4.87598 12.0475 4.80354 12.1764 4.67461C12.3054 4.54568 12.3778 4.37081 12.3778 4.18848V2.81348C12.3778 2.63114 12.3054 2.45627 12.1764 2.32734C12.0475 2.19841 11.8726 2.12598 11.6903 2.12598H10.3153C10.133 2.12598 9.9581 2.19841 9.82917 2.32734C9.70024 2.45627 9.62781 2.63114 9.62781 2.81348V4.18848C9.62781 4.37081 9.70024 4.54568 9.82917 4.67461C9.9581 4.80354 10.133 4.87598 10.3153 4.87598H11.6903ZM0.00280762 13.8135C0.00280762 13.2665 0.220106 12.7419 0.6069 12.3551C0.993694 11.9683 1.5183 11.751 2.06531 11.751H3.44031C3.98732 11.751 4.51192 11.9683 4.89872 12.3551C5.28551 12.7419 5.50281 13.2665 5.50281 13.8135V15.1885C5.50281 15.7355 5.28551 16.2601 4.89872 16.6469C4.51192 17.0337 3.98732 17.251 3.44031 17.251H2.06531C1.5183 17.251 0.993694 17.0337 0.6069 16.6469C0.220106 16.2601 0.00280762 15.7355 0.00280762 15.1885V13.8135ZM2.06531 13.126C1.88297 13.126 1.7081 13.1984 1.57917 13.3273C1.45024 13.4563 1.37781 13.6311 1.37781 13.8135V15.1885C1.37781 15.3708 1.45024 15.5457 1.57917 15.6746C1.7081 15.8035 1.88297 15.876 2.06531 15.876H3.44031C3.62264 15.876 3.79751 15.8035 3.92644 15.6746C4.05537 15.5457 4.12781 15.3708 4.12781 15.1885V13.8135C4.12781 13.6311 4.05537 13.4563 3.92644 13.3273C3.79751 13.1984 3.62264 13.126 3.44031 13.126H2.06531ZM8.25281 13.8135C8.25281 13.2665 8.47011 12.7419 8.8569 12.3551C9.24369 11.9683 9.7683 11.751 10.3153 11.751H11.6903C12.2373 11.751 12.7619 11.9683 13.1487 12.3551C13.5355 12.7419 13.7528 13.2665 13.7528 13.8135V15.1885C13.7528 15.7355 13.5355 16.2601 13.1487 16.6469C12.7619 17.0337 12.2373 17.251 11.6903 17.251H10.3153C9.7683 17.251 9.24369 17.0337 8.8569 16.6469C8.47011 16.2601 8.25281 15.7355 8.25281 15.1885V13.8135ZM10.3153 13.126C10.133 13.126 9.9581 13.1984 9.82917 13.3273C9.70024 13.4563 9.62781 13.6311 9.62781 13.8135V15.1885C9.62781 15.3708 9.70024 15.5457 9.82917 15.6746C9.9581 15.8035 10.133 15.876 10.3153 15.876H11.6903C11.8726 15.876 12.0475 15.8035 12.1764 15.6746C12.3054 15.5457 12.3778 15.3708 12.3778 15.1885V13.8135C12.3778 13.6311 12.3054 13.4563 12.1764 13.3273C12.0475 13.1984 11.8726 13.126 11.6903 13.126H10.3153ZM16.5028 13.8135C16.5028 13.2665 16.7201 12.7419 17.1069 12.3551C17.4937 11.9683 18.0183 11.751 18.5653 11.751H19.9403C20.4873 11.751 21.0119 11.9683 21.3987 12.3551C21.7855 12.7419 22.0028 13.2665 22.0028 13.8135V15.1885C22.0028 15.7355 21.7855 16.2601 21.3987 16.6469C21.0119 17.0337 20.4873 17.251 19.9403 17.251H18.5653C18.0183 17.251 17.4937 17.0337 17.1069 16.6469C16.7201 16.2601 16.5028 15.7355 16.5028 15.1885V13.8135ZM18.5653 13.126C18.383 13.126 18.2081 13.1984 18.0792 13.3273C17.9502 13.4563 17.8778 13.6311 17.8778 13.8135V15.1885C17.8778 15.3708 17.9502 15.5457 18.0792 15.6746C18.2081 15.8035 18.383 15.876 18.5653 15.876H19.9403C20.1226 15.876 20.2975 15.8035 20.4264 15.6746C20.5554 15.5457 20.6278 15.3708 20.6278 15.1885V13.8135C20.6278 13.6311 20.5554 13.4563 20.4264 13.3273C20.2975 13.1984 20.1226 13.126 19.9403 13.126H18.5653Z"
              fill="white"
            />
          </svg>
          Data Visualizations
        </div>
        <div className="Gallery-Filter-bar">
          <div className="GallerySearchContainer">
            <input
              className="GallerySearchBox"
              type="text"
              placeholder="Author, Title, Topic, Keyword"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="search-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M16.1481 14.2239C17.4795 12.4071 18.0758 10.1545 17.8177 7.91695C17.5597 5.67936 16.4663 3.62171 14.7562 2.15567C13.0462 0.689637 10.8457 -0.0766745 8.59497 0.0100511C6.34422 0.0967767 4.2092 1.03014 2.61707 2.62342C1.02493 4.21669 0.0930909 6.35238 0.0079759 8.60319C-0.0771391 10.854 0.690747 13.054 2.15801 14.7629C3.62527 16.4719 5.6837 17.5638 7.92147 17.8203C10.1592 18.0768 12.4113 17.4788 14.2272 16.1461H14.2258C14.2671 16.2011 14.3111 16.2534 14.3606 16.3042L19.6543 21.598C19.9121 21.856 20.2619 22.001 20.6266 22.0011C20.9914 22.0013 21.3413 21.8565 21.5993 21.5987C21.8573 21.3409 22.0023 20.9911 22.0024 20.6264C22.0025 20.2616 21.8578 19.9117 21.5999 19.6537L16.3062 14.36C16.257 14.3102 16.2042 14.2643 16.1481 14.2225V14.2239ZM16.5028 8.93837C16.5028 9.93149 16.3072 10.9149 15.9272 11.8324C15.5471 12.7499 14.9901 13.5836 14.2878 14.2859C13.5856 14.9881 12.7519 15.5452 11.8344 15.9252C10.9168 16.3053 9.93344 16.5009 8.94032 16.5009C7.9472 16.5009 6.9638 16.3053 6.04628 15.9252C5.12875 15.5452 4.29507 14.9881 3.59283 14.2859C2.89058 13.5836 2.33353 12.7499 1.95348 11.8324C1.57343 10.9149 1.37782 9.93149 1.37782 8.93837C1.37782 6.93267 2.17458 5.00912 3.59283 3.59088C5.01107 2.17263 6.93462 1.37587 8.94032 1.37587C10.946 1.37587 12.8696 2.17263 14.2878 3.59088C15.7061 5.00912 16.5028 6.93267 16.5028 8.93837V8.93837Z"
                  fill="#765623"
                />
              </svg>
            </button>
          </div>
          <div className="GalleryFilters">
            {uniqueCategories.map((category) => (
              <div key={category} className="Gallery-select">
                <label>
                  <input
                    type="checkbox"
                    checked={!!selectedCategories[category]}
                    onChange={() => handleCategoryChange(category)}
                  />{" "}
                  {category}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!isIntermediateView ? (
        <div className="Gallery-Right">
          <div className="GalleryToggles">
            <div
              className="gallery-right-heading"
              style={{ display: "flex", gap: "1rem" }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.25281 4.81348C8.25281 4.26647 8.47011 3.74186 8.8569 3.35507C9.24369 2.96828 9.7683 2.75098 10.3153 2.75098H11.6903C12.2373 2.75098 12.7619 2.96828 13.1487 3.35507C13.5355 3.74186 13.7528 4.26647 13.7528 4.81348V6.18848C13.7528 6.73549 13.5355 7.26009 13.1487 7.64688C12.7619 8.03368 12.2373 8.25098 11.6903 8.25098V9.62598H19.2528C19.4351 9.62598 19.61 9.69841 19.7389 9.82734C19.8679 9.95627 19.9403 10.1311 19.9403 10.3135V11.6885C19.9403 11.8708 19.8679 12.0457 19.7389 12.1746C19.61 12.3035 19.4351 12.376 19.2528 12.376C19.0705 12.376 18.8956 12.3035 18.7667 12.1746C18.6377 12.0457 18.5653 11.8708 18.5653 11.6885V11.001H11.6903V11.6885C11.6903 11.8708 11.6179 12.0457 11.4889 12.1746C11.36 12.3035 11.1851 12.376 11.0028 12.376C10.8205 12.376 10.6456 12.3035 10.5167 12.1746C10.3877 12.0457 10.3153 11.8708 10.3153 11.6885V11.001H3.44031V11.6885C3.44031 11.8708 3.36787 12.0457 3.23894 12.1746C3.11001 12.3035 2.93514 12.376 2.75281 12.376C2.57047 12.376 2.3956 12.3035 2.26667 12.1746C2.13774 12.0457 2.06531 11.8708 2.06531 11.6885V10.3135C2.06531 10.1311 2.13774 9.95627 2.26667 9.82734C2.3956 9.69841 2.57047 9.62598 2.75281 9.62598H10.3153V8.25098C9.7683 8.25098 9.24369 8.03368 8.8569 7.64688C8.47011 7.26009 8.25281 6.73549 8.25281 6.18848V4.81348ZM11.6903 6.87598C11.8726 6.87598 12.0475 6.80354 12.1764 6.67461C12.3054 6.54568 12.3778 6.37081 12.3778 6.18848V4.81348C12.3778 4.63114 12.3054 4.45627 12.1764 4.32734C12.0475 4.19841 11.8726 4.12598 11.6903 4.12598H10.3153C10.133 4.12598 9.9581 4.19841 9.82917 4.32734C9.70024 4.45627 9.62781 4.63114 9.62781 4.81348V6.18848C9.62781 6.37081 9.70024 6.54568 9.82917 6.67461C9.9581 6.80354 10.133 6.87598 10.3153 6.87598H11.6903ZM0.00280762 15.8135C0.00280762 15.2665 0.220106 14.7419 0.6069 14.3551C0.993694 13.9683 1.5183 13.751 2.06531 13.751H3.44031C3.98732 13.751 4.51192 13.9683 4.89872 14.3551C5.28551 14.7419 5.50281 15.2665 5.50281 15.8135V17.1885C5.50281 17.7355 5.28551 18.2601 4.89872 18.6469C4.51192 19.0337 3.98732 19.251 3.44031 19.251H2.06531C1.5183 19.251 0.993694 19.0337 0.6069 18.6469C0.220106 18.2601 0.00280762 17.7355 0.00280762 17.1885V15.8135ZM2.06531 15.126C1.88297 15.126 1.7081 15.1984 1.57917 15.3273C1.45024 15.4563 1.37781 15.6311 1.37781 15.8135V17.1885C1.37781 17.3708 1.45024 17.5457 1.57917 17.6746C1.7081 17.8035 1.88297 17.876 2.06531 17.876H3.44031C3.62264 17.876 3.79751 17.8035 3.92644 17.6746C4.05537 17.5457 4.12781 17.3708 4.12781 17.1885V15.8135C4.12781 15.6311 4.05537 15.4563 3.92644 15.3273C3.79751 15.1984 3.62264 15.126 3.44031 15.126H2.06531ZM8.25281 15.8135C8.25281 15.2665 8.47011 14.7419 8.8569 14.3551C9.24369 13.9683 9.7683 13.751 10.3153 13.751H11.6903C12.2373 13.751 12.7619 13.9683 13.1487 14.3551C13.5355 14.7419 13.7528 15.2665 13.7528 15.8135V17.1885C13.7528 17.7355 13.5355 18.2601 13.1487 18.6469C12.7619 19.0337 12.2373 19.251 11.6903 19.251H10.3153C9.7683 19.251 9.24369 19.0337 8.8569 18.6469C8.47011 18.2601 8.25281 17.7355 8.25281 17.1885V15.8135ZM10.3153 15.126C10.133 15.126 9.9581 15.1984 9.82917 15.3273C9.70024 15.4563 9.62781 15.6311 9.62781 15.8135V17.1885C9.62781 17.3708 9.70024 17.5457 9.82917 17.6746C9.9581 17.8035 10.133 17.876 10.3153 17.876H11.6903C11.8726 17.876 12.0475 17.8035 12.1764 17.6746C12.3054 17.5457 12.3778 17.3708 12.3778 17.1885V15.8135C12.3778 15.6311 12.3054 15.4563 12.1764 15.3273C12.0475 15.1984 11.8726 15.126 11.6903 15.126H10.3153ZM16.5028 15.8135C16.5028 15.2665 16.7201 14.7419 17.1069 14.3551C17.4937 13.9683 18.0183 13.751 18.5653 13.751H19.9403C20.4873 13.751 21.0119 13.9683 21.3987 14.3551C21.7855 14.7419 22.0028 15.2665 22.0028 15.8135V17.1885C22.0028 17.7355 21.7855 18.2601 21.3987 18.6469C21.0119 19.0337 20.4873 19.251 19.9403 19.251H18.5653C18.0183 19.251 17.4937 19.0337 17.1069 18.6469C16.7201 18.2601 16.5028 17.7355 16.5028 17.1885V15.8135ZM18.5653 15.126C18.383 15.126 18.2081 15.1984 18.0792 15.3273C17.9502 15.4563 17.8778 15.6311 17.8778 15.8135V17.1885C17.8778 17.3708 17.9502 17.5457 18.0792 17.6746C18.2081 17.8035 18.383 17.876 18.5653 17.876H19.9403C20.1226 17.876 20.2975 17.8035 20.4264 17.6746C20.5554 17.5457 20.6278 17.3708 20.6278 17.1885V15.8135C20.6278 15.6311 20.5554 15.4563 20.4264 15.3273C20.2975 15.1984 20.1226 15.126 19.9403 15.126H18.5653Z"
                  fill="#1A1A1A"
                />
              </svg>
              <p className="featured-subtitle">Data Visualizations</p>
            </div>

            <div className="GalleryToggleButtons">
              <TagButton
                category="Show All"
                isSelected={Object.keys(selectedCategories).length === 0}
                onClick={handleShowAllClick}
              >
                Show All
              </TagButton>
              {uniqueCategories.map((category) => (
                <TagButton
                  key={category}
                  category={category}
                  isSelected={!!selectedCategories[category]}
                  onClick={() => handleTagButtonClick(category)}
                >
                  {category}
                </TagButton>
              ))}
            </div>
          </div>
          <div className="Gallery-Results">
            {filteredLinks.map((link, index) => (
              <div className="gallery-R" key={index}>
                <div
                  onClick={() => handleLinkClick(link)}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={link.image}
                    alt={link.title}
                    style={{ width: 150, height: 150, objectFit: "cover" }}
                  />
                  <p>{link.title}</p>
                  <div className="tagbuttons">
                    {link.categories.map((category, categoryIndex) => (
                      <TagButton
                        key={categoryIndex}
                        category={category}
                        onClick={(e) => {
                          e.stopPropagation(); // Stops the click event from bubbling up to the parent elements
                          handleTagButtonClick(category);
                        }}
                      >
                        {category}
                      </TagButton>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="Podcast-render">
          {selectedLink && (
            <>
              <div className="information">
                <div className="pod-info-left">
                  <img
                    src={selectedLink.image}
                    alt={selectedLink.title}
                    style={{ width: 250, height: 250, objectFit: "cover" }}
                    onClick={() =>
                      handleIntermediateLinkClick(
                        selectedLink.url,
                        selectedLink.id
                      )
                    }
                    className="show-popup-button"
                  ></img>
                  <div className="topic-button-container">
                    <div className="actions">
                      <TagButton onClick={handleCiteClick}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M15 15C15.3315 15 15.6494 14.8683 15.8839 14.6339C16.1183 14.3995 16.25 14.0815 16.25 13.75V10.6975C16.25 10.366 16.1183 10.048 15.8839 9.81362C15.6494 9.5792 15.3315 9.4475 15 9.4475H13.265C13.265 9.00875 13.2912 8.56875 13.3425 8.13C13.42 7.665 13.55 7.25125 13.73 6.89C13.9112 6.5275 14.1437 6.24375 14.4287 6.03625C14.7125 5.80375 15.0737 5.6875 15.5137 5.6875V3.75C14.79 3.75 14.1575 3.905 13.6137 4.215C13.0747 4.52186 12.6113 4.94556 12.2575 5.455C11.9024 6.01664 11.6407 6.63212 11.4825 7.2775C11.3231 7.9897 11.2451 8.7177 11.25 9.4475V13.75C11.25 14.0815 11.3817 14.3995 11.6161 14.6339C11.8505 14.8683 12.1685 15 12.5 15H15ZM7.49997 15C7.83149 15 8.14944 14.8683 8.38386 14.6339C8.61828 14.3995 8.74997 14.0815 8.74997 13.75V10.6975C8.74997 10.366 8.61828 10.048 8.38386 9.81362C8.14944 9.5792 7.83149 9.4475 7.49997 9.4475H5.76497C5.76497 9.00875 5.79122 8.56875 5.84247 8.13C5.91997 7.665 6.04997 7.25125 6.22997 6.89C6.41122 6.5275 6.64372 6.24375 6.92872 6.03625C7.21247 5.80375 7.57372 5.6875 8.01372 5.6875V3.75C7.28997 3.75 6.65747 3.905 6.11372 4.215C5.57471 4.52186 5.11128 4.94556 4.75747 5.455C4.40238 6.01664 4.14065 6.63212 3.98247 7.2775C3.82308 7.9897 3.74508 8.7177 3.74997 9.4475V13.75C3.74997 14.0815 3.88167 14.3995 4.11609 14.6339C4.35051 14.8683 4.66845 15 4.99997 15H7.49997Z"
                            fill="#765623"
                          />
                        </svg>
                      </TagButton>
                      <TagButton>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <g clipPath="url(#clip0_2090_572)">
                            <path
                              d="M11 2.50007C10.9999 1.9135 11.2061 1.34559 11.5825 0.895681C11.9588 0.445776 12.4814 0.142533 13.0588 0.039007C13.6361 -0.0645194 14.2315 0.0382633 14.7408 0.329372C15.25 0.620481 15.6406 1.08138 15.8444 1.63143C16.0481 2.18148 16.0519 2.78565 15.8552 3.33824C15.6585 3.89084 15.2737 4.35666 14.7682 4.65421C14.2627 4.95177 13.6687 5.06211 13.0901 4.96592C12.5115 4.86974 11.9851 4.57316 11.603 4.12807L4.88502 7.24807C5.03946 7.73751 5.03946 8.26263 4.88502 8.75207L11.603 11.8721C12.0069 11.4024 12.5707 11.0994 13.1852 11.0217C13.7997 10.9441 14.4212 11.0973 14.9292 11.4518C15.4372 11.8062 15.7955 12.3366 15.9347 12.9402C16.0739 13.5438 15.9841 14.1775 15.6826 14.7186C15.3812 15.2598 14.8896 15.6697 14.3031 15.869C13.7166 16.0683 13.077 16.0429 12.5083 15.7975C11.9396 15.5521 11.4822 15.1043 11.2247 14.5409C10.9673 13.9775 10.9282 13.3386 11.115 12.7481L4.39702 9.62807C4.06446 10.0156 3.62117 10.2921 3.1268 10.4202C2.63243 10.5482 2.11069 10.5219 1.63176 10.3445C1.15283 10.1672 0.739697 9.8475 0.447928 9.42836C0.15616 9.00921 -0.000244141 8.51077 -0.000244141 8.00007C-0.000244141 7.48937 0.15616 6.99093 0.447928 6.57178C0.739697 6.15264 1.15283 5.8329 1.63176 5.65559C2.11069 5.47828 2.63243 5.45189 3.1268 5.57998C3.62117 5.70808 4.06446 5.9845 4.39702 6.37207L11.115 3.25207C11.0386 3.00874 10.9998 2.75514 11 2.50007Z"
                              fill="#765623"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_2090_572">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </TagButton>
                      <TagButton>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <mask
                            id="mask0_1431_35317"
                            maskUnits="userSpaceOnUse"
                            x="0"
                            y="0"
                            width="20"
                            height="20"
                          >
                            <rect width="20" height="20" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_1431_35317)">
                            <path
                              d="M4.16675 13.3334V9.16675C4.16675 8.93064 4.24661 8.73272 4.40633 8.573C4.56605 8.41328 4.76397 8.33342 5.00008 8.33342C5.23619 8.33342 5.43411 8.41328 5.59383 8.573C5.75355 8.73272 5.83341 8.93064 5.83341 9.16675V13.3334C5.83341 13.5695 5.75355 13.7674 5.59383 13.9272C5.43411 14.0869 5.23619 14.1667 5.00008 14.1667C4.76397 14.1667 4.56605 14.0869 4.40633 13.9272C4.24661 13.7674 4.16675 13.5695 4.16675 13.3334ZM9.16675 13.3334V9.16675C9.16675 8.93064 9.24661 8.73272 9.40633 8.573C9.56605 8.41328 9.76397 8.33342 10.0001 8.33342C10.2362 8.33342 10.4341 8.41328 10.5938 8.573C10.7536 8.73272 10.8334 8.93064 10.8334 9.16675V13.3334C10.8334 13.5695 10.7536 13.7674 10.5938 13.9272C10.4341 14.0869 10.2362 14.1667 10.0001 14.1667C9.76397 14.1667 9.56605 14.0869 9.40633 13.9272C9.24661 13.7674 9.16675 13.5695 9.16675 13.3334ZM2.50008 17.5001C2.26397 17.5001 2.06605 17.4202 1.90633 17.2605C1.74661 17.1008 1.66675 16.9029 1.66675 16.6667C1.66675 16.4306 1.74661 16.2327 1.90633 16.073C2.06605 15.9133 2.26397 15.8334 2.50008 15.8334H17.5001C17.7362 15.8334 17.9341 15.9133 18.0938 16.073C18.2536 16.2327 18.3334 16.4306 18.3334 16.6667C18.3334 16.9029 18.2536 17.1008 18.0938 17.2605C17.9341 17.4202 17.7362 17.5001 17.5001 17.5001H2.50008ZM14.1667 13.3334V9.16675C14.1667 8.93064 14.2466 8.73272 14.4063 8.573C14.5661 8.41328 14.764 8.33342 15.0001 8.33342C15.2362 8.33342 15.4341 8.41328 15.5938 8.573C15.7536 8.73272 15.8334 8.93064 15.8334 9.16675V13.3334C15.8334 13.5695 15.7536 13.7674 15.5938 13.9272C15.4341 14.0869 15.2362 14.1667 15.0001 14.1667C14.764 14.1667 14.5661 14.0869 14.4063 13.9272C14.2466 13.7674 14.1667 13.5695 14.1667 13.3334ZM17.5001 6.66675H2.41675C2.20841 6.66675 2.03133 6.59383 1.8855 6.448C1.73966 6.30217 1.66675 6.12508 1.66675 5.91675V5.45842C1.66675 5.30564 1.70494 5.17369 1.78133 5.06258C1.85772 4.95147 1.95841 4.86119 2.08341 4.79175L9.25008 1.20841C9.48619 1.0973 9.73619 1.04175 10.0001 1.04175C10.264 1.04175 10.514 1.0973 10.7501 1.20841L17.8751 4.77091C18.0279 4.84036 18.1424 4.94453 18.2188 5.08342C18.2952 5.2223 18.3334 5.36814 18.3334 5.52092V5.83342C18.3334 6.06953 18.2536 6.26744 18.0938 6.42717C17.9341 6.58689 17.7362 6.66675 17.5001 6.66675Z"
                              fill="#765623"
                            />
                          </g>
                        </svg>
                      </TagButton>
                    </div>
                  </div>
                </div>
                <div className="pod-info-right">
                  <p className="pod-title">{selectedLink.title}</p>
                  <div className="pod-info-right-meta"></div>
                  <div className="topic-button-container">
                    {selectedLink.categories &&
                      selectedLink.categories.map((category, index) => (
                        <TagButton key={index}>{category}</TagButton>
                      ))}
                  </div>
                  <p className="pod-disc">{selectedLink.title}</p>
                </div>
              </div>

              <div className="related-sections">
                <RelatedDatavis dataItem={selectedLink} />
                <RelatedNovelsForDatavis dataItem={selectedLink} />
                <RelatedBriefsForDatavis dataItem={selectedLink} />
                <RelatedPodcastsForDatavis dataItem={selectedLink} />
                <RelatedArchivesForDatavis dataItem={selectedLink} />
              </div>
            </>
          )}
        </div>
      )}

      {isPopupVisible && (
        <div className="PopupOverlay">
          <div className="PopupContent">
            <iframe
              src={iframeUrl}
              name="iframe-content"
              width="100%"
              height="100%"
            ></iframe>
            <button className="CloseButton" onClick={closePopup}>
              X
            </button>
          </div>
        </div>
      )}

      {isCitationVisible && (
        <div className="citation-modal">
          <div className="citation-content">
            <button
              onClick={handleCloseCitation}
              className="close-citation-button"
            >
              X
            </button>
            <div className="citation-styles">
              <button
                onClick={() => setCitationStyle("MLA")}
                className={citationStyle === "MLA" ? "active" : ""}
              >
                MLA
              </button>
              <button
                onClick={() => setCitationStyle("APA")}
                className={citationStyle === "APA" ? "active" : ""}
              >
                APA
              </button>
              <button
                onClick={() => setCitationStyle("Chicago")}
                className={citationStyle === "Chicago" ? "active" : ""}
              >
                Chicago
              </button>
            </div>
            <div className="citation-right">
              <div className="citation-text">
                <p>{generateCitation(citationStyle)}</p>
              </div>
              <button
                onClick={handleCopyToClipboard}
                className="copy-citation-button"
              >
                Copy to Clipboard
              </button>
              <p>
                <b>
                  Remember to check citations for accuracy before including them
                  in your work!
                </b>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataHome;
